import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

/**
 * Wraps request with loading and data
 *
 * Example:
 * ```js
 * const { data: bibleStudies } = useAsyncRequest(
 *  useCallback(() => request<BibleStudy[]>(apiConfigs.getBibleStudies(defaultChurchId)), [request])
 * );
 * ```
 *
 * @param runRequest wrapped in useCallback hook
 * @param runCallback wrapped in useCallback hook
 * @param runErrorCallback wrapped in useCallback hook
 */
export const useAsyncRequest = <T>(
  runRequest: () => Promise<T>,
  runCallback?: <Response>(response: Response) => void,
  runErrorCallback?: (error: any) => void
) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<T | null>(null);

  // To prevent updates on unmount
  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await runRequest();
        if (cancelled || !isMounted.current) return;
        setData(response);
        try {
          await Promise.resolve(runCallback?.(response as never));
        } catch (callbackError: any) {
          if (isMounted.current) {
            if (runErrorCallback) {
              runErrorCallback(callbackError);
            } else {
              toast.error(callbackError?.message || 'Something went wrong!');
            }
          }
        }
      } catch (error: any) {
        if (isMounted.current) {
          if (runErrorCallback) {
            runErrorCallback(error);
          } else {
            toast.error(error.message || 'Something went wrong!');
          }
        }
      } finally {
        if (isMounted.current) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runCallback, runErrorCallback, runRequest]);

  return {
    loading,
    data,
  };
};
